import { boundedPromise } from "@-/dev.improve.haniverse.lib.deadline";

/**
 * Can this browser run the persistent database the app requires?
 *
 * Two independent requirements, and the old OPFS-only probe checked just one:
 *
 *   1. **OPFS with sync access handles.** Absent in a private/incognito tab on some
 *      engines, in Firefox Android, and in in-app webviews.
 *   2. **Cross-origin isolation** (`crossOriginIsolated` + `SharedArrayBuffer`). The
 *      Turso wasm engine is threaded; without COOP/COEP headers it throws
 *      `SharedArrayBuffer transfer requires self.crossOriginIsolated` from deep
 *      inside `loadSyncWasm()` — a stack trace that reads like an app bug rather
 *      than a deployment misconfiguration.
 *
 * Reporting WHICH one failed matters: "this browser isn't supported" is a dead end
 * for someone self-hosting who simply needs to add two response headers.
 *
 * The cheap synchronous checks run first so a misconfigured deployment fails fast,
 * without paying for an OPFS round-trip.
 */
export type TursoWebSupport =
  | { reason: "no-cross-origin-isolation" | "no-opfs"; supported: false }
  | { supported: true };

/**
 * Bound on the OPFS probe. A working browser answers in single-digit ms when the machine is idle —
 * but this spawns a WORKER and does real OPFS I/O, and neither is bounded by the app. Under load,
 * worker startup alone can exceed the old 2s.
 *
 * 2s -> 30s (2026-08-07). A timeout here is NOT treated as "slow", it is treated as UNSUPPORTED:
 * the bounded probe rejects, the `catch` returns false, and the whole app is replaced by
 * `UnsupportedBrowserScreen` ("This browser isn't supported"). So an over-tight bound does not
 * degrade the experience, it DENIES SERVICE to a perfectly capable browser.
 *
 * Observed in dev-CI run 31227230486, `web maestro (shard 6)`, `web-browser-history.yaml`: the app
 * rendered the Levels screen perfectly (screenshot `history-after-back.png` — "All Levels" pill,
 * every level populated), then minutes later, SAME browser and SAME origin, an `openLink /stats`
 * full page load hit the unsupported screen. Nothing about the browser's capabilities changed; the
 * host was saturated (the migration watchdog logged single steps taking +2s, +3s and +5s in that
 * same run). CI merely surfaces this — a real user on a slow or busy device hits the identical
 * false negative, and for them it is a hard, unrecoverable dead end.
 *
 * 30s is chosen to be far outside any plausible slow-but-working case while still bounding a
 * genuinely broken browser (which fails fast via the `catch`/`error` paths, not via this timeout).
 */
const OPFS_PROBE_TIMEOUT_MS = 30_000;

/**
 * Probe body, run INSIDE a worker (see `canOpenOpfsSyncAccessHandle`). Opens and
 * closes one sync access handle on a uniquely-named throwaway file — unique so there
 * is no lock contention, making this fast on a working browser and fast-failing on a
 * broken one.
 */
const OPFS_PROBE_WORKER_SOURCE = `
self.onmessage = async () => {
  const name = ".han-support-probe-" + Math.random().toString(36).slice(2, 10);
  try {
    const root = await navigator.storage.getDirectory();
    const fileHandle = await root.getFileHandle(name, { create: true });
    if (typeof fileHandle.createSyncAccessHandle !== "function") {
      self.postMessage(false);
      return;
    }
    const accessHandle = await fileHandle.createSyncAccessHandle();
    accessHandle.close();
    try { await root.removeEntry(name); } catch {}
    self.postMessage(true);
  } catch {
    self.postMessage(false);
  }
};
`;

export async function checkTursoWebSupport(): Promise<TursoWebSupport> {
  if (!hasCrossOriginIsolation()) {
    return { reason: "no-cross-origin-isolation", supported: false };
  }
  if (!(await canOpenOpfsSyncAccessHandle())) {
    return { reason: "no-opfs", supported: false };
  }
  return { supported: true };
}

function hasCrossOriginIsolation(): boolean {
  return (
    Reflect.get(globalThis, "crossOriginIsolated") === true &&
    typeof Reflect.get(globalThis, "SharedArrayBuffer") === "function"
  );
}

/**
 * Functionally probe OPFS rather than sniffing for the API surface: some engines
 * expose `navigator.storage.getDirectory` and then refuse to grant a sync access
 * handle.
 *
 * THE PROBE MUST RUN INSIDE A WORKER. `createSyncAccessHandle` exists only on OPFS
 * file handles obtained on a WORKER thread — on the main thread the method is
 * `undefined` even in a fully capable browser. A main-thread probe therefore reports
 * "unsupported" for EVERY browser and gates the entire app behind the
 * unsupported-browser screen. (Measured against the dev stack: Chromium with
 * `crossOriginIsolated === true` and a working OPFS still yields
 * `typeof createSyncAccessHandle === "undefined"` on the main thread — the app booted
 * fine and opened `guest.db`, yet the gate covered it with "This browser isn't
 * supported".)
 *
 * The worker is built from a Blob so this needs no bundler support and no public
 * asset — the gate must work even when the web prebuild hasn't run.
 */
async function canOpenOpfsSyncAccessHandle(): Promise<boolean> {
  const storage = globalThis.navigator?.storage;
  if (storage?.getDirectory == null) {
    return false;
  }
  if (typeof Worker !== "function" || typeof Blob !== "function" || typeof URL !== "function") {
    return false;
  }

  let objectUrl: string | undefined;
  let worker: Worker | undefined;
  try {
    objectUrl = URL.createObjectURL(
      new Blob([OPFS_PROBE_WORKER_SOURCE], { type: "text/javascript" }),
    );
    const probeWorker = new Worker(objectUrl);
    worker = probeWorker;
    return await boundedPromise<boolean>(
      { deadline: OPFS_PROBE_TIMEOUT_MS, operation: "[check-turso-web-support] OPFS probe" },
      (resolve) => {
        const onMessage = (event: MessageEvent) => {
          resolve(event.data === true);
        };
        const onError = () => {
          resolve(false);
        };
        probeWorker.addEventListener("message", onMessage);
        probeWorker.addEventListener("error", onError);
        probeWorker.postMessage("probe");
        return () => {
          probeWorker.removeEventListener("message", onMessage);
          probeWorker.removeEventListener("error", onError);
        };
      },
    );
  } catch {
    return false;
  } finally {
    worker?.terminate();
    if (objectUrl !== undefined) {
      URL.revokeObjectURL(objectUrl);
    }
  }
}
